import Omit from "lodash/omit";
import React from "react";
import Select, { OnChangeValue, SelectInstance } from "react-select";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";

import fetchSingleton from "@/lib/fetchSingleton";
import { humanizeError } from "@/lib/humanizeError";
import { useAuth } from "@/lib/useAuth";
import { useDataList } from "@/lib/useData";
import { ApiResponseDto } from "@/types/api-types";

import styles from "./PartAddressSuggestions.module.scss";

type Suggestion = { Text: string; PlaceId: string };
type SelectItem = { label: string; value: string };

export type AddressSuggestion = {
  postal_code?: string;
  street_full_name?: string;
  street_number?: string;
  lat?: string;
  lon?: string;
  city?: string;
  unit?: string;
  program_id?: number;
};

type PartAddressSuggestionsProps = {
  id?: string;
  onChange: (addressSuggestion: AddressSuggestion) => void;
};

const PartAddressSuggestions: React.FC<PartAddressSuggestionsProps> = ({
  id,
  onChange,
}) => {
  const { idToken } = useAuth();

  const [suggestions, setSuggestions] = React.useState<SelectItem[]>([]);

  const [isLoadingSuggestion, setIsLoadingSuggestion] = React.useState<boolean>(false);
  const [isSettingSuggestion, setIsSettingSuggestion] = React.useState<boolean>(false);

  const [programsItems] = useDataList<{ id: number; name: string }>(
    process.env.NEXT_PUBLIC_API_URL + "/api/v1/program",
  );

  const fetchPromiseRef = React.useRef<ReturnType<typeof fetchSingleton>>(undefined);

  const fetchSuggestions = React.useCallback(
    async (inputValue: string) => {
      if (inputValue?.length < 1) throw new Error("Backend don't support empty string");

      const fetchPromise = fetchSingleton(
        process.env.NEXT_PUBLIC_API_URL +
          `/api/v1/address/autocomplete?query=${encodeURIComponent(inputValue)}`,
        {
          channelId: "autocomplete-suggestions",
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );

      fetchPromiseRef.current = fetchPromise;

      const result = (await fetchPromise) as ApiResponseDto<Suggestion>;

      fetchPromiseRef.current = undefined;

      if (result.status === "OK") {
        setSuggestions(
          result?.list?.map(
            it =>
              ({
                label: ("Text" in it && it.Text) || "unknown format",
                value: it.PlaceId,
              }) satisfies SelectItem,
          ),
        );
      } else {
        toast.error(humanizeError(result));
        setSuggestions([]);
      }
      setIsLoadingSuggestion(false);
    },
    [idToken],
  );

  const fetchSuggestionsDebounced = useDebouncedCallback(fetchSuggestions, 1000);

  const getSuggestions = React.useCallback(
    async (inputValue: string) => {
      // Canceling previous request as not actual
      fetchPromiseRef.current?.cancel();
      // Only ui effect
      await new Promise(resolve => setTimeout(resolve, 250));
      if (inputValue?.length < 3) {
        setIsLoadingSuggestion(false);
        return;
      }
      setIsLoadingSuggestion(true);
      fetchSuggestionsDebounced(inputValue);
      // fetchSuggestions internally calls setIsLoadingSuggestion(false) when it finishes
    },
    [fetchSuggestionsDebounced],
  );

  const handleSelectSuggestion = React.useCallback(
    async (selected: OnChangeValue<SelectItem, false>) => {
      if (!selected) return;

      setIsSettingSuggestion(true);

      const result = (await fetchSingleton(
        process.env.NEXT_PUBLIC_API_URL +
          `/api/v1/address/place-details?place_id=${selected.value}`,
        {
          channelId: "autocomplete-fill",
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      )) as ApiResponseDto<{
        PostalCode?: string;
        Street?: string;
        AddressNumber?: string;
        Geometry?: { Point: [string, string] };
        Municipality?: string;
        UnitNumber?: string;
        Region?: string;
      }>;

      if (result.status === "OK") {
        const item = result?.list?.[0];

        onChange({
          // Here we added empty string to force overwrite old data in forms
          city: item?.Municipality || "",
          street_full_name: item?.Street || "",
          street_number: item?.AddressNumber || "",
          postal_code: item?.PostalCode || "",
          lat: item?.Geometry?.Point?.[1] || "",
          lon: item?.Geometry?.Point?.[0] || "",
          unit: item?.UnitNumber || "",
          program_id: programsItems?.find(
            it => it?.name?.toLowerCase() === item?.Region?.toLowerCase(),
          )?.id,
        });
      } else {
        toast.error(humanizeError(result));
      }

      setIsSettingSuggestion(false);
    },
    [idToken, programsItems, onChange],
  );

  const [inputValue, setInputValue] = React.useState("");
  const [value, setValue] = React.useState<{ label: string; value: string } | null>(null);
  const selectRef = React.useRef<SelectInstance<{ label: string; value: string }> | null>(
    null,
  );

  // TODO Create pending or loader when programsItemsLoading

  return (
    <div data-testid="formAddressAutocomplete">
      <Select
        id={id}
        ref={selectRef}
        inputValue={inputValue}
        onInputChange={(newValue, { action }) => {
          /**
           *
           * Object { newValue: "t", action: "input-change" }
           * Object { newValue: "to", action: "input-change" }
           * Object { newValue: "tor", action: "input-change" }
           * Object { newValue: "toro", action: "input-change" }
           * Object { newValue: "toron", action: "input-change" }
           * Object { newValue: "toront", action: "input-change" }
           * Object { newValue: "toronto", action: "input-change" }
           * Object { newValue: "", action: "input-blur" }
           * Object { newValue: "", action: "menu-close" }
           *
           * Object { newValue: "", action: "set-value" }
           * Object { newValue: "", action: "menu-close" }
           */
          if (action === "input-change") {
            setInputValue(newValue);
            void getSuggestions(newValue);
          }
          if (action === "set-value") {
            setInputValue("");
          }
        }}
        onMenuClose={() => {
          selectRef.current?.blur();
        }}
        value={value}
        options={suggestions}
        isLoading={isLoadingSuggestion || isSettingSuggestion}
        filterOption={() => true} // disables filtering
        onChange={v => {
          setValue(v);
          handleSelectSuggestion(v).then();
        }}
        classNamePrefix={"autocomplete"}
        placeholder={"Type something, minimum 3 symbols"}
        classNames={{
          container: () => styles.selectBox,
          control: () => styles.selectContainer,
          menu: () => styles.menuBox,
          menuList: () => styles.menuContainer,
          option: () => styles.selectOption,
          multiValue: () => styles.selectValueItem,
          valueContainer: () => styles.selectValueContainer,
          singleValue: () => styles.singleValue,
        }}
        styles={{
          option: () => ({}),
          multiValue: () => ({}),
          multiValueLabel: () => ({}),
          menu: base => ({
            ...Omit(base, ["boxShadow", "border", "borderRadius"]),
            marginTop: 1,
          }),
          control: () => ({}),
        }}
      />
    </div>
  );
};

export default PartAddressSuggestions;
